import React, { forwardRef } from 'react';
import { Paper, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius } from '../../../theme/theme';

export interface PanelShellProps {
  children: React.ReactNode;
  borderPosition?: 'left' | 'right' | 'none';
  width?: string | number | object;
  role?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  ref?: React.Ref<HTMLDivElement>;
  sx?: SxProps<Theme>;
}

const PanelShell = forwardRef<HTMLDivElement, PanelShellProps>(
  function PanelShell(
    {
      children,
      borderPosition = 'left',
      width,
      role,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      sx,
      ...rest
    },
    ref
  ) {
    const theme = useTheme();

    const borderProp =
      borderPosition === 'left'
        ? 'borderLeft'
        : borderPosition === 'right'
          ? 'borderRight'
          : undefined;

    return (
      <Paper
        ref={ref}
        elevation={0}
        role={role}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        sx={[
          {
            borderRadius: `${borderRadius.none}px`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%',
            width,
            ...(borderProp
              ? { [borderProp]: `1px solid ${theme.palette.divider}` }
              : {}),
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...rest}
      >
        {children}
      </Paper>
    );
  }
);

PanelShell.displayName = 'PanelShell';

export default PanelShell;
